import React, { useState } from 'react'; // Removed useEffect
import { Table, Spin, Alert, Typography, Descriptions, Tooltip, Input, Button, Modal, message } from 'antd'; // Removed unused imports
import { FilePdfOutlined, LoadingOutlined } from '@ant-design/icons';
// import axios from 'axios'; // No longer needed here
// import { calculateDynamicTarget, calculateZScore_V2, calculateAverageAbsoluteZScore, calculateSystemAndLifestyleDeviations } from '../utils/scoringEngineV2'; // No longer needed here
// import * as math from 'mathjs'; // No longer needed here
import useHealthSnapshotData from '../hooks/useHealthSnapshotData'; // Import the custom hook
import ParameterWeightsModal from './ParameterWeightsModal'; // Import the new modal
import PatientFlowsheet from './PatientFlowsheet'; // Import PatientFlowsheet
import BiologicalAgeBreakdown from './BiologicalAgeBreakdown'; // Import the new breakdown component
import { exportMedicalRecordPDF, downloadPDF } from '../services/pdfExportService';
import { getPatientActionPlans, getActionPlanById } from '../services/actionPlanService';


const { Title, Text } = Typography;

const HealthSnapshotV2 = ({ patientId }) => {
  const {
    refetch,
    loading,
    error,
    tableData,
    overallDeviation,
    systemDeviations,
    lifestyleDeviations,
    coreLifestyleComponents,
    allParamsConfig, // This should contain the aggregated parameter weights
    patientDemographics, // Available from hook for patient demographics
    biologicalAge, // Biological age calculation results
  } = useHealthSnapshotData(patientId);

  const [searchTerm, setSearchTerm] = useState('');
  const [weightsModalVisible, setWeightsModalVisible] = useState(false);
  const [isFlowsheetVisible, setIsFlowsheetVisible] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);


  const handleValueClick = (parameterKey) => {
    // Use the full standardized parameter key for consistent navigation
    // This ensures PatientFlowsheet can properly highlight the parameter
    setSelectedParameter(parameterKey);
    setIsFlowsheetVisible(true);
  };

  // Export PDF functionality moved to ComprehensiveHealthDashboard (Health Overview tab)
  // This keeps the detailed metrics tab focused on data analysis only

  console.log('[HealthSnapshotV2] 🔍 Component received data:', {
    loading,
    error,
    tableDataLength: tableData?.length,
    tableDataType: Array.isArray(tableData) ? 'array' : typeof tableData,
    overallDeviation,
    hasSystemDeviations: !!systemDeviations,
    hasLifestyleDeviations: !!lifestyleDeviations,
    hasAllParamsConfig: !!allParamsConfig
  });

  const getZScoreColor = (zScore) => {
    if (typeof zScore !== 'number' || isNaN(zScore)) {
      return 'inherit'; // Default color or handle as N/A
    }
    const absZ = Math.abs(zScore);
    if (absZ < 1) return 'green';
    if (absZ < 2) return 'orange';
    return 'red';
  };

  const primaryColumns = [
    {
      title: 'Parameter Name',
      dataIndex: 'parameterName',
      key: 'parameterName',
      fixed: 'left',
      width: 300,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            Key: <code>{record.key}</code>
          </div>
          {record.backendKey && record.backendKey !== record.key && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '1px' }}>
              Backend: <code>{record.backendKey}</code>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Last Value',
      dataIndex: 'currentValue',
      key: 'currentValue',
      width: 120,
      render: (val, record) => {
        // Check if there's an "Awaiting data" error
        if (record.error === "Awaiting data") {
          return <Text type="secondary" italic>Awaiting data</Text>;
        }
        const displayValue = val?.toFixed ? val.toFixed(2) : (val === null || val === undefined ? 'N/A' : val);
        // Display the actual value if available
        return (
            <a onClick={() => handleValueClick(record.key)} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                {displayValue}
            </a>
        );
      }
    },
    { 
      title: 'Ideal Target (T)', dataIndex: 'idealTarget', key: 'idealTarget', width: 130,
      render: (val) => val?.toFixed ? val.toFixed(2) : (val === null || val === undefined ? 'N/A' : val)
    },
    { 
      title: 'Z-Score', dataIndex: 'zScore', key: 'zScore', width: 120,
      render: (val) => {
        const displayVal = val?.toFixed ? `${val.toFixed(2)}σ` : (val === 0 ? '0.00σ' : (val === null || val === undefined ? 'N/A' : val));
        return (
          <Text style={{ color: getZScoreColor(val), fontWeight: typeof val === 'number' ? 'bold' : 'normal' }}>{displayVal}</Text>
        );
      }
    },
    {
      title: 'Total Impact', // Renamed from 'Total'
      dataIndex: 'weightedAbsoluteZScore',
      key: 'weightedAbsoluteZScore',
      width: 120,
      render: (val) => (typeof val === 'number' && !isNaN(val)) ? val.toFixed(3) : 'N/A'
    },
    { 
      title: 'Error', dataIndex: 'error', key: 'error', width: 200, ellipsis: true,
      render: (text) => {
        if (text === "Awaiting data") {
          return <Text type="secondary" italic>{text}</Text>;
        }
        if (text) {
          return <Text type="danger">{text}</Text>;
        }
        return null;
      }
    },
  ];

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 150px)' }}><Spin tip="Loading Detailed Health Metrics..." size="large" /></div>;
  }

  if (error) {
    return <Alert message="Error Loading Detailed Health Metrics" description={error} type="error" showIcon style={{ margin: 20 }} />;
  }

  if (!tableData || !Array.isArray(tableData)) {
    console.log('[HealthSnapshotV2] ⚠️ No tableData available - showing warning');
    return <Alert message="No Data Available" description="Table data is not available. Please check the data source." type="warning" showIcon style={{ margin: 20 }} />;
  }

  console.log('[HealthSnapshotV2] ✅ About to render with tableData, length:', tableData.length);

  let filteredTableData = [];
  try {
    filteredTableData = tableData.map(categoryGroup => {
      if (!searchTerm.trim()) { // Use trim() to handle empty or whitespace-only search
        return categoryGroup;
      }
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filteredParameters = categoryGroup.parameters.filter(param => {
        // Ensure param and param.parameterName are valid before trying to filter
        if (param && typeof param.parameterName === 'string') {
          return param.parameterName.toLowerCase().includes(lowerSearchTerm);
        }
        return false; // If no parameterName, don't include it
      });
      return { ...categoryGroup, parameters: filteredParameters };
    }).filter(categoryGroup => categoryGroup.parameters.length > 0);
    console.log('[HealthSnapshotV2] ✅ Filtered table data prepared, groups:', filteredTableData.length);
  } catch (filterError) {
    console.error('[HealthSnapshotV2] ❌ Error filtering table data:', filterError);
    return <Alert message="Error Processing Data" description={`Failed to filter table data: ${filterError.message}`} type="error" showIcon style={{ margin: 20 }} />;
  }

  console.log('[HealthSnapshotV2] ✅ About to render main component');

  return (
    <div style={{ padding: '20px' }}>
      <Title level={3} style={{ marginBottom: '20px' }}>Health Snapshot V2 - Detailed Z-Score Calculations</Title>

      {/* Insert the new Biological Age Breakdown component here */}
      <div style={{ marginBottom: 24 }}>
        {console.log('[HealthSnapshotV2] 📊 Rendering BiologicalAgeBreakdown...')}
        <BiologicalAgeBreakdown patientId={patientId} />
        {console.log('[HealthSnapshotV2] ✅ BiologicalAgeBreakdown rendered')}
      </div>

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder="Search parameters by name..."
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '400px' }}
        />
        <div>
          <Button onClick={() => setWeightsModalVisible(true)} style={{ marginRight: 8 }}>
            View Parameter Weight Configuration
          </Button>
          <Button onClick={refetch} type="primary">
            Refresh Data
          </Button>
        </div>
      </div>








      {overallDeviation.average !== null && !isNaN(overallDeviation.average) && (
        <Descriptions bordered column={1} size="small" style={{ marginBottom: 20 }}>
          <Descriptions.Item 
            label={
              <Tooltip 
                title={
                  <pre>{`Sum of Abs Zs: ${overallDeviation.sumOfAbsoluteZScores?.toFixed(2) || 'N/A'}\nParameter Count: ${overallDeviation.count}\n\nFormula: (Sum of Abs Zs) / Count\n(${overallDeviation.sumOfAbsoluteZScores?.toFixed(2) || 'N/A'}) / ${overallDeviation.count || 'N/A'} = ${overallDeviation.average.toFixed(2)}`}</pre>
                }
                placement="top"
              >
                Total Average Absolute Z-Score Deviation
              </Tooltip>
            }
          >
            <Text strong>{overallDeviation.average.toFixed(2)}σ</Text> (based on {overallDeviation.count} parameters)
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Iterate over grouped filteredTableData */}
      {Array.isArray(filteredTableData) && filteredTableData.map(group => (
        <div key={group.categoryName} style={{ marginBottom: '30px' }}>
          <Title level={4} style={{ marginBottom: '10px', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px' }}>
            {group.categoryName} ({group.parameters ? group.parameters.length : 0})
          </Title>
          <Table
            columns={primaryColumns}
            dataSource={group.parameters}
            rowKey="key" // Assuming 'key' is unique within each parameter object
            bordered
            size="small"
            scroll={{ x: 900 }} // Adjusted scroll for primary columns + error
            pagination={false} // Disable pagination for individual category tables
            expandable={{
              expandedRowRender: (record) => {
                // Enhanced calculation transparency with step-by-step mathematical details
                const formatValue = (val) => val?.toFixed ? val.toFixed(2) : (val === null || val === undefined ? 'N/A' : val);
                const formatSigma = (val) => val?.toFixed ? val.toFixed(1) : (val === null || val === undefined ? 'N/A' : val);

                // Generate detailed Z-Score calculation steps
                const generateZScoreSteps = () => {
                  if (record.currentValue === null || record.idealTarget === null || record.selectedSigma === null) {
                    return 'Cannot calculate Z-Score: Missing current value, ideal target, or standard deviation';
                  }

                  const current = parseFloat(record.currentValue);
                  const target = parseFloat(record.idealTarget);
                  const sigma = parseFloat(record.selectedSigma);
                  const diff = current - target;
                  const zScore = diff / sigma;

                  return `Step 1: Calculate difference from target
Current Value - Ideal Target = ${current.toFixed(2)} - ${target.toFixed(2)} = ${diff.toFixed(2)}

Step 2: Divide by standard deviation
Z-Score = (Current - Target) / σ
Z-Score = ${diff.toFixed(2)} / ${sigma.toFixed(2)} = ${zScore.toFixed(3)}σ

Step 3: Absolute Z-Score for health impact
|Z-Score| = |${zScore.toFixed(3)}| = ${Math.abs(zScore).toFixed(3)}σ`;
                };

                const calculationDetails = (
                  <Descriptions bordered column={1} size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                    <Descriptions.Item label="Ideal Target (T) Formula - Complete Mathematical Work">
                      <div style={{ backgroundColor: '#f0f8ff', padding: '12px', borderRadius: '4px' }}>
                        <Title level={5} style={{ marginTop: 0 }}>Ideal Target Calculation Breakdown</Title>
                        <Text strong>Formula Type: </Text><Text>{record.targetFormulaUsed || 'N/A'}</Text>
                        <div style={{ marginTop: '10px' }}>
                          {(record.targetCalculationSteps && Array.isArray(record.targetCalculationSteps)) ? (
                            record.targetCalculationSteps.map((step, index) => (
                              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #e8e8e8' }}>
                                <Text style={{ flex: 1 }}>{step.term}</Text>
                                <Text code style={{ flex: 2, textAlign: 'center' }}>{step.calculation}</Text>
                                <Text strong style={{ flex: 1, textAlign: 'right' }}>{typeof step.value === 'number' ? step.value.toFixed(2) : 'N/A'}</Text>
                              </div>
                            ))
                          ) : (
                            <Text type="secondary">No detailed steps available.</Text>
                          )}
                        </div>
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #1890ff' }}>
                          <Text strong>Final Formula: </Text>
                          <Text code>{record.targetFinalFormula || 'N/A'}</Text>
                        </div>
                      </div>
                    </Descriptions.Item>

                    <Descriptions.Item label="Z-Score Formula - Step-by-Step Calculation">
                      <div style={{ backgroundColor: '#f6ffed', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
                        <Text strong style={{ color: '#52c41a' }}>Z-Score Formula:</Text>
                        <pre style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                          Z-Score = (Current Value - Ideal Target) / Standard Deviation
                        </pre>
                      </div>
                      <div style={{ backgroundColor: '#fff', padding: '8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                        <Text strong>Mathematical Steps:</Text>
                        <pre style={{ margin: '4px 0 0 0', whiteSpace: 'pre-wrap', fontSize: '12px', lineHeight: '1.4' }}>
                          {generateZScoreSteps()}
                        </pre>
                      </div>
                    </Descriptions.Item>

                    <Descriptions.Item label="Clinical Values & Calculation Breakdown">
                      <Descriptions bordered column={2} size="small" style={{ backgroundColor: '#fff' }}>
                        <Descriptions.Item label="Current Value">{formatValue(record.currentValue)}</Descriptions.Item>
                        <Descriptions.Item label="Ideal Target">{formatValue(record.idealTarget)}</Descriptions.Item>
                        <Descriptions.Item label="σ (Low Direction)">{formatSigma(record.sigmaLow)}</Descriptions.Item>
                        <Descriptions.Item label="σ (High Direction)">{formatSigma(record.sigmaHigh)}</Descriptions.Item>
                        <Descriptions.Item label="Selected σ">
                          <Tooltip title={<pre>{record.selectedSigmaReason || 'N/A'}</pre>} placement="top">
                            <span style={{ borderBottom: '1px dashed #1890ff', cursor: 'help' }}>
                              {formatSigma(record.selectedSigma)}
                            </span>
                          </Tooltip>
                        </Descriptions.Item>
                        <Descriptions.Item label="Difference (Current - Target)">
                           {formatValue(record.diffFromTarget)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Z-Score (Signed)">
                           {record.zScore?.toFixed ? `${record.zScore.toFixed(3)}σ` : 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Absolute Z-Score">
                           {record.absoluteZScore?.toFixed ? `${record.absoluteZScore.toFixed(3)}σ` : (record.absoluteZScore === 0 ? '0.000σ' : 'N/A')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Overall Health Weight">
                          {typeof record.overall_significance_weight === 'number' && !isNaN(record.overall_significance_weight) ? `${record.overall_significance_weight.toFixed(0)}%` : 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Weighted Impact Score">
                          {record.weightedAbsoluteZScore?.toFixed ? record.weightedAbsoluteZScore.toFixed(4) : 'N/A'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Descriptions.Item>
                  </Descriptions>
                );

                // Existing individual weights breakdown logic
                const { individual_weights, zScore: baseZScore, absoluteZScore: absZScore } = record;
                const subRows = [];
                const sfrDisplayNames = { S: "Structure", F: "Function", R: "Risk" };
                const renderCell = (content, width, textAlign = 'left', key) => (
                  <div key={key} style={{ display: 'table-cell', width: width ? `${width}px` : 'auto', padding: '4px 6px', verticalAlign: 'top', borderBottom: '1px solid #eee', textAlign: textAlign }}>
                    {content}
                  </div>
                );
                // Adjusted column widths for the sub-table (individual weights)
                const colWidths = { paramName: 200, absZ: 120, weight: 100 }; 
                const emptySpan1Width = primaryColumns.find(c => c.key === 'currentValue').width + primaryColumns.find(c => c.key === 'idealTarget').width;
                const emptySpan2Width = primaryColumns.find(c => c.key === 'zScore').width;


                if (individual_weights && typeof absZScore === 'number') {
                  const { system_weights, lifestyle_weights } = individual_weights;
                  if (system_weights && Object.keys(system_weights).length > 0) {
                    subRows.push(
                      <div key="system-header" style={{ display: 'table-row', fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                        {renderCell('System Contributions:', colWidths.paramName, 'left', 'sh-pn')}
                        {renderCell('', emptySpan1Width, 'left', 'sh-es1')}
                        {renderCell('', emptySpan2Width, 'left', 'sh-es2')}
                        {renderCell('Weighted Abs Z', colWidths.absZ, 'right', 'sh-az')}
                        {renderCell('Indiv. Weight', colWidths.weight, 'right', 'sh-w')}
                      </div>
                    );
                    for (const [systemName, sfrWeights] of Object.entries(system_weights)) {
                      const capitalizedSystemName = systemName.charAt(0).toUpperCase() + systemName.slice(1);
                      Object.entries(sfrWeights).forEach(([sfrKey, weightVal]) => {
                        if (weightVal !== undefined && ['S', 'F', 'R'].includes(sfrKey.toUpperCase())) {
                          const displayName = sfrDisplayNames[sfrKey.toUpperCase()] || sfrKey.toUpperCase();
                          subRows.push(
                            <div key={`${systemName}-${sfrKey}`} style={{ display: 'table-row' }}>
                              {renderCell(`${capitalizedSystemName} - ${displayName} (${sfrKey.toUpperCase()})`, colWidths.paramName, 'left', `sys-${systemName}-${sfrKey}-label`)}
                              {renderCell('', emptySpan1Width, 'left', `sys-${systemName}-${sfrKey}-es1`)}
                              {renderCell('', emptySpan2Width, 'left', `sys-${systemName}-${sfrKey}-es2`)}
                              {renderCell((absZScore * (weightVal / 100)).toFixed(3), colWidths.absZ, 'right', `sys-${systemName}-${sfrKey}-waz`)}
                              {renderCell(typeof weightVal === 'number' ? `${weightVal.toFixed(0)}%` : 'N/A', colWidths.weight, 'right', `sys-${systemName}-${sfrKey}-iw`)}
                            </div>
                          );
                        }
                      });
                    }
                  }

                  if (lifestyle_weights && Object.keys(lifestyle_weights).length > 0) {
                    subRows.push(
                      <div key="lifestyle-header" style={{ display: 'table-row', fontWeight: 'bold', backgroundColor: '#f0f0f0', marginTop: '5px' }}>
                        {renderCell('Lifestyle Contributions:', colWidths.paramName, 'left', 'lh-pn')}
                        {renderCell('', emptySpan1Width, 'left', 'lh-es1')}
                        {renderCell('', emptySpan2Width, 'left', 'lh-es2')}
                        {renderCell('Weighted Abs Z', colWidths.absZ, 'right', 'lh-az')}
                        {renderCell('Indiv. Weight', colWidths.weight, 'right', 'lh-w')}
                      </div>
                    );
                    for (const [domainName, domainWeight] of Object.entries(lifestyle_weights)) {
                      const capitalizedDomainName = domainName.charAt(0).toUpperCase() + domainName.slice(1);
                      subRows.push(
                        <div key={domainName} style={{ display: 'table-row' }}>
                          {renderCell(capitalizedDomainName, colWidths.paramName, 'left', `ls-${domainName}-label`)}
                          {renderCell('', emptySpan1Width, 'left', `ls-${domainName}-es1`)}
                          {renderCell('', emptySpan2Width, 'left', `ls-${domainName}-es2`)}
                          {renderCell((absZScore * (domainWeight / 100)).toFixed(3), colWidths.absZ, 'right', `ls-${domainName}-waz`)}
                          {renderCell(typeof domainWeight === 'number' ? `${domainWeight.toFixed(0)}%` : 'N/A', colWidths.weight, 'right', `ls-${domainName}-iw`)}
                        </div>
                      );
                    }
                  }
                }
                
                const individualWeightsSection = (individual_weights && (Object.keys(individual_weights.system_weights || {}).length > 0 || Object.keys(individual_weights.lifestyle_weights || {}).length > 0) && typeof absZScore === 'number') ? (
                  <div style={{ marginTop: 16, padding: '8px 0px', backgroundColor: '#f9f9f9', display: 'table', width: '100%', borderCollapse: 'collapse' }}>
                    <div style={{ display: 'table-header-group', fontWeight: 'bold' }}>
                      <div style={{ display: 'table-row' }}>
                        {renderCell(`Base Z-Score for ${record.parameterName}: ${typeof baseZScore === 'number' ? baseZScore.toFixed(2) : 'N/A'}σ`, '100%', 'left', 'basez-header')}
                      </div>
                    </div>
                    <div style={{ display: 'table-row-group' }}>
                      {subRows.length > 0 ? subRows : <div style={{ display: 'table-row' }}><div style={{display: 'table-cell', padding: '8px'}}><Text type="secondary">No individual system or lifestyle weights defined for this parameter.</Text></div></div>}
                    </div>
                  </div>
                ) : <Text type="secondary" style={{ padding: '10px 0px', display: 'block' }}>No detailed weight breakdown available or Z-Score missing for individual contributions.</Text>;

                return (
                  <div style={{ padding: '12px', backgroundColor: '#fdfdfd' }}>
                    {calculationDetails}
                    {individualWeightsSection}
                  </div>
                );
              },
              rowExpandable: () => true, // All rows are expandable to show calculation details
            }}
          />
        </div>
      ))}

      {/* System Deviations section removed as it's redundant with Health Overview Tab */}

      {/* Lifestyle Domain Deviations section removed as it's redundant with Health Overview Tab and its modal */}

      <ParameterWeightsModal
        visible={weightsModalVisible}
        onCancel={() => setWeightsModalVisible(false)}
        aggregatedWeightsData={allParamsConfig}
      />

      <Modal
        title={`Historical Data for ${selectedParameter}`}
        open={isFlowsheetVisible}
        onCancel={() => setIsFlowsheetVisible(false)}
        footer={null}
        width="90vw"
        destroyOnClose={true}
      >
        <PatientFlowsheet patientId={patientId} initialFilterKey={selectedParameter} />
      </Modal>
    </div>
  );
};

export default HealthSnapshotV2;
